import type { UpdateUserPreferencesClientsVariables, WebClientPreference } from '@ms/yammer-graphql';
import mutationUpdateUserPreferences from '@ms/yammer-graphql/dist/operations/mutationUpdateUserPreferences.graphql';

import type { GraphqlApiRequest, ManagedApiLifecycle } from '../..';
import { getGraphqlApiRequest, managedApiAction } from '../..';
import { normalizeUserPreferences } from '../../normalization/graphql/normalizeUserPreferences';
import type { User } from '../../state/types';
import type { PromiseThunkAction } from '../../types';

import type {
  UpdateUserPreferencesFulfilledAction,
  UpdateUserPreferencesPendingAction,
  UpdateUserPreferencesRejectedAction,
  UpdateUserPreferencesResponse,
} from './actions.types';

const updateUserPreferencesPending = (id: User['id']): UpdateUserPreferencesPendingAction => ({
  type: 'USER_PREFERENCES.UPDATE_USER_PREFERENCES_PENDING',
  payload: {
    id,
  },
});

const updateUserPreferencesFulfilled = (
  userPreferences: WebClientPreference,
  id: User['id']
): UpdateUserPreferencesFulfilledAction => ({
  type: 'USER_PREFERENCES.UPDATE_USER_PREFERENCES_FULFILLED',
  payload: {
    preference: userPreferences,
    id,
  },
});

const updateUserPreferencesRejected = (error: unknown, id: User['id']): UpdateUserPreferencesRejectedAction => ({
  type: 'USER_PREFERENCES.UPDATE_USER_PREFERENCES_REJECTED',
  payload: {
    error,
    id,
  },
  error: true,
});

type UpdateUserPreferencesLifecycle = (userId: User['id']) => ManagedApiLifecycle<UpdateUserPreferencesResponse>;
const updateUserPreferencesLifecycle: UpdateUserPreferencesLifecycle = (userId) => ({
  pending: () => updateUserPreferencesPending(userId),
  fulfilled: (response) => updateUserPreferencesFulfilled(normalizeUserPreferences(response), userId),
  rejected: (error) => updateUserPreferencesRejected(error, userId),
});

export const updateUserPreferences = (
  userPreferences: UpdateUserPreferencesClientsVariables,
  userId: User['id']
): PromiseThunkAction<void> => {
  const request: GraphqlApiRequest<UpdateUserPreferencesClientsVariables> = getGraphqlApiRequest({
    body: {
      operationName: 'UpdateUserPreferencesClients',
      query: mutationUpdateUserPreferences,
      variables: userPreferences,
    },
  });
  const lifecycle = updateUserPreferencesLifecycle(userId);

  return managedApiAction({ request, lifecycle });
};
